/**
 * Run membership — "do these two cabinetry pieces belong to the same wall run?"
 *
 * Lifted verbatim from `functions/features/post-processing/kitchen.js` (where it
 * had been the private predicate every kitchen run rule leaned on) so the
 * client-side scan-run deriver can group scanned cabinets the same way the
 * server's post-processing rules already do. Both callers MUST agree: the server
 * finishes a run with this notion of membership, and the deriver then has to
 * recover exactly that run from the objects it left behind.
 *
 * See claude-docs/2026-08-24-scanned-kitchen-cabinet-runs.md §4.3.
 *
 * Pure. No I/O.
 */

const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { objectFootprint } = require('../geometry/objectFootprint');

const IN = 0.0254;

// Two pieces this far apart perpendicular to the wall are not on the same run.
// Only reached when the wall ids differ; a shared `parentWallIds[0]` settles
// it, but only once the two pieces are known to be parallel (see onSameWall).
const SAME_WALL_PERP_TOLERANCE = 24 * IN;

/**
 * The unit vector a cabinet's WIDTH runs along, in the XZ plane.
 *
 * `dimensions.x` is the object's own width, so for `rotation.y = θ` it points
 * along `(cos θ, -sin θ)` — Three.js's local +X, which is what the stored
 * rotations mean and what every renderer draws.
 *
 * NOTE THE NEGATED SIN. `calculateCorners` uses the MIRROR of this,
 * `(cos θ, +sin θ)`, so any footprint built from it must be handed
 * `-rotation.y` (which is exactly what `getObjectCorners` does). This
 * function used to return the mirror too, and the two agree wherever
 * `sin θ == 0` — every cardinal wall, which is why it read as correct for
 * so long. On structure 08E54F9E's -105.9 degree wall the two are 137
 * degrees apart, and since `setAlongWallSpan` TRANSLATES along this vector,
 * `alignRunEnds` turned a 13.7" widening into a 9.2cm shove perpendicular to
 * the wall, burying the run 6.4cm inside it and going deeper every pass.
 *
 * This frame is shared with `post-processing/run-spans.js`'s `axisFor` — a run
 * measured in one is written back through the other. They move together or not
 * at all; see the frame note at the top of that file.
 */
function wallAxis(obj) {
  const theta = obj?.rotation?.y ?? 0;
  return { cos: Math.cos(theta), sin: -Math.sin(theta) };
}

/**
 * An object's offset PERPENDICULAR to a wall axis.
 *
 * `ref` supplies the frame. Measuring each object in its OWN frame is wrong
 * for anti-parallel pieces: two runs facing each other across a room have
 * rotations differing by pi, so they produce offsets of equal magnitude and
 * opposite sign and compare as co-located. In structure 26767201 a fridge on
 * the east wall (perp +53) matched the west wall's cabinet (perp +52) 105"
 * away and trimmed it from 114" to 13.4".
 */
function perpOffset(obj, ref = obj) {
  const { cos, sin } = wallAxis(ref);
  return -(obj?.center?.x || 0) * sin + (obj?.center?.z || 0) * cos;
}

/**
 * True when two pieces belong to the same wall run.
 *
 * A 1-D along-wall coordinate says nothing about WHICH wall a piece is on, so
 * every neighbour query has to establish this first. Without it the island's
 * cabinets counted as run neighbours in structure 0D8A06BE and slid the oven
 * column 27.7" off the run — the island is 106" away perpendicular.
 */
function onSameWall(a, b) {
  // Parallel FIRST, on every path — including a shared wall id, which used to
  // return true on its own. "Same run" has to mean "same FRAME": every rule
  // that consumes a run measures it with `alongWallSpan` and moves it with
  // `setAlongWallSpan`, and both project onto each piece's OWN facing axis.
  // A member turned across the run therefore contributes a scalar from a
  // different axis to the run's extent, and travels along that axis when the
  // run slides. In structure 66616D52 the 45-degree corner range shared its
  // wall id with the cabinets, so `closeRunToWall` read the wall's reveal as
  // 7.13" instead of 10.23" and walked the range 7.13" diagonally out of the
  // corner — undoing the corner-range rectification every reprocess.
  // Antiparallel counts: a wall is routinely modelled facing the other way.
  const parallel =
    Math.abs(Math.cos((a?.rotation?.y ?? 0) - (b?.rotation?.y ?? 0))) >= 0.99;
  if (!parallel) return false;

  const aw = a?.parentWallIds?.[0];
  const bw = b?.parentWallIds?.[0];
  if (aw && bw && aw === bw) return true;
  // Differing ids do NOT settle it: one physical run is routinely split across
  // several wall segments (structure 0D8A06BE's run spans 42B52FEE and
  // 5096E266), so fall back to geometry — the same distance out from the wall.
  // The island, 106" off the run, still fails this.
  // Both measured in ONE frame, so a facing wall reads as far away, not near.
  return (
    Math.abs(perpOffset(a, a) - perpOffset(b, a)) <= SAME_WALL_PERP_TOLERANCE
  );
}

// Lives beside `onSameWall` because it answers the other half of the same
// question — membership asks whether two bodies share a wall, this asks
// whether one body can be MEASURED in its wall's frame at all. Moved here
// from `scanRuns.js` (which requires it back) when the vanity deriver needed
// the same gate; a second copy of a test with this history would be the worst
// kind of duplication.
/**
 * True when `obj`'s footprint is axis-aligned with `wall` — its width runs along
 * the wall, or (a cabinet turned into a return) across it.
 *
 * A body sitting at an angle to its own parent wall cannot be measured in that
 * wall's frame: `memberSpanOnWall` projects the centre and extends by half the
 * WIDTH, which is only the true extent when the two axes agree. A 45-degree
 * corner range is the real case — post-processing rectifies one against two
 * walls, and it keeps a `parentWallIds[0]` — and folding it in would hand the
 * run an extent measured on the wrong axis.
 *
 * The extent of a rectangle along any direction is `w·|ex·u| + d·|ez·u|`, which
 * equals exactly `w` or `d` when the box is axis-aligned to `u` and strictly
 * exceeds both otherwise. So rather than reason about angles, measure the real
 * projected extent of the footprint and check it against the two side lengths.
 *
 * The footprint MUST come from `wallMath.objectFootprint`, never from
 * `geometry.calculateCorners(center, w, d, rotation.y)`. That helper maps local
 * +X to `(cos, sin)`, while every kitchen builder stores the rotation
 * `getWallAxes` computes, whose local +X is `(cos, -sin)` — the opposite
 * handedness (the #1506 trap; see run-spans.js's note on conventions). Passing a
 * stored rotation to it REFLECTS the rectangle. On an axis-aligned wall the
 * reflection maps the rect onto itself and costs nothing, so the test looked
 * correct for its whole life; on a wall θ off the world axes it inflates the
 * measured extent by ~2θ. Structure 08E54F9E's range sits on a 15.9-degree wall:
 * a 30 x 25" box measured 38.7" (ratio 1.289), failed this gate, and was dropped
 * from run membership entirely — so its run carried NO range bay and the fill
 * packed three base cabinets and a 96.6" countertop straight through the
 * appliance. Same defect on 886A9359 (wall at 164.9 degrees).
 */
const AXIS_ALIGNED_TOL = 1.05; // 5% — comfortably below 45 degrees' 1/cos(45)

function isAxisAlignedToWall(obj, wall) {
  const axes = getWallAxes(wall);
  const w = obj?.dimensions?.x || 0;
  const d = obj?.dimensions?.z || 0;
  if (!axes || w <= 0) return false;
  const corners = objectFootprint(obj);
  if (!corners) return false;
  const proj = corners.map((c) => c.x * axes.wallDir.x + c.z * axes.wallDir.z);
  const extent = Math.max(...proj) - Math.min(...proj);
  return extent <= Math.max(w, d) * AXIS_ALIGNED_TOL;
}

module.exports = {
  wallAxis,
  perpOffset,
  onSameWall,
  SAME_WALL_PERP_TOLERANCE,
  isAxisAlignedToWall,
  AXIS_ALIGNED_TOL,
};
